import React from 'react';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-client';
import { Tooltip } from '@mui/material';

import { ReactComponent as GreenCheckmark } from 'assets/icons/GreenCheckmark.svg';
import { ReactComponent as BlueCheckmark } from 'assets/icons/BlueCheckmark.svg';
import { ReactComponent as InformationIcon } from 'assets/icons/InformationIcon.svg';
import { getSkippableTillDisplayText } from 'utils/skippableFieldsUtils';

import { useStyles } from '../styles';

interface ISidebarStepProps {
  step: {
    title: string;
    isCompleted?: boolean;
    isClickable?: boolean;
    disabled?: boolean;
    fields?: {
      name: string;
      id: string;
      isRequired?: boolean;
      isCompleted?: boolean;
      isSkippable?: boolean;
      skippableTill?: number;
    }[];
    icon: React.ReactNode;
  };
  index: number; // Absolute index in WIZARD_STEPS, not filtered array index
  currentStepIndex: number;
  disableNavigation?: boolean;
  onStepChange?: (stepIndex: number) => void;
}

const SidebarStep: React.FC<ISidebarStepProps> = ({
  step,
  index,
  currentStepIndex,
  disableNavigation,
  onStepChange,
}) => {
  const classes = useStyles();
  const isActive = index === currentStepIndex;
  const hasFieldsToShow = step.fields?.filter(field => field.isRequired || field.isSkippable)?.length > 0;

  const canClick = !step.disabled && !isActive && !disableNavigation && step.isClickable;
  const isStepDisabled = step.disabled || (disableNavigation && !isActive);

  return (
    <div
      className={`${classes.sidebarStep} ${
        !isActive || !hasFieldsToShow ? classes.sidebarStepCenter : ''
      } ${step.isCompleted ? '' : classes.noCursor} ${isStepDisabled ? classes.sidebarStepDisabled : ''} ${
        isActive ? classes.sidebarStepActive : ''
      }`}
      onClick={() => canClick && onStepChange?.(index)}
    >
      <div className={classes.stepIcon}>{step.icon}</div>
      <div className={classes.stepTitleParent}>
        <p
          className={`${classes.stepTitle} ${step.isCompleted && !isActive ? classes.stepTitleCompleted : ''} ${isActive ? classes.stepTitleActive : ''}`}
        >
          {step.title}
        </p>
        <AnimatePresence initial={true}>
          {hasFieldsToShow && isActive && (
            <motion.section
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{
                duration: 0.4,
                ease: [0.04, 0.62, 0.23, 0.98],
              }}
            >
              <div className={classes.stepFields}>
                {step.fields
                  .filter(field => field.isRequired || field.isSkippable)
                  .map(field => (
                    <div key={field.id} className={classes.fieldContainer}>
                      <p className={`${classes.field}`}>{field.name}</p>
                      {field.isSkippable && field.skippableTill > 0 && (
                        <Tooltip
                          title={`Skippable till ${getSkippableTillDisplayText(field.skippableTill)}`}
                          classes={{
                            popper: classes.tooltipPopper,
                            tooltip: classes.tooltip,
                            arrow: classes.tooltipArrow,
                          }}
                          arrow
                          placement="top"
                          disableTouchListener
                        >
                          <div className={classes.fieldIcon}>
                            <InformationIcon />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
      {step.isCompleted && !isActive && <GreenCheckmark />}
      {isActive && <BlueCheckmark />}
    </div>
  );
};

export default SidebarStep;
